import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// import Captcha, {
//   CAPTCHA_ENABLED,
//   withCaptchaHeader,
// } from 'components/LoggingOperations/Captcha';

const AuthContext = createContext();

class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => {
    const accessToken = localStorage.getItem('accessToken');
    const groups = localStorage.getItem('groups');
    return {
      isLoggedIn: !!accessToken,
      groups: groups
        ? Array.isArray(groups)
          ? groups
          : groups.split(',').filter(Boolean)
        : ['default'],
      permissions: [],
      isLoading: true,
    };
  });

  // const captchaRef = React.useRef(null);

  const login = async (credentials) => {
    // const captchaToken = CAPTCHA_ENABLED
    //   ? captchaRef.current?.getToken?.()
    //   : null;
    // const headers = withCaptchaHeader(
    //   { 'Content-Type': 'application/json' },
    //   captchaToken,
    // );
    const headers = { 'Content-Type': 'application/json' };
    const response = await fetch(
      `${process.env.REACT_APP_LORAX_API_BASE_URL}/login/`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(credentials),
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.detail || 'Failed to login';

      throw new ApiError(message, {
        status: response.status,
        data: errorData,
      });
    }
    const responseData = await response.json();
    const data = responseData.data;

    if (data?.need2fa) {
      const pending = data.pendingToken;
      localStorage.setItem('pendingToken', pending);
      window.location.href = '/2fa-verify';
      return { status: '2FA_REQUIRED' };
    }

    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('refreshToken', data.refresh);
    localStorage.setItem('groups', data.groups);

    setAuthState({
      isLoggedIn: true,
      groups: Array.isArray(data.groups)
        ? data.groups
        : data.groups
          ? data.groups.split(',').filter(Boolean)
          : ['default'],
      permissions: data.permissions || [],
      isLoading: false,
    });
    return { status: 'OK' };
  };

  const applyTokens = ({ access, refresh, groups = [], permissions = [] }) => {
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    localStorage.setItem(
      'groups',
      Array.isArray(groups) ? groups.join(',') : groups || '',
    );

    setAuthState({
      isLoggedIn: true,
      groups: Array.isArray(groups)
        ? groups
        : groups
          ? groups.split(',').filter(Boolean)
          : ['default'],
      permissions,
      isLoading: false,
    });
  };

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('groups');
    setAuthState({
      isLoggedIn: false,
      groups: [],
      permissions: [],
      isLoading: false,
    });
  }, []);

  const validateToken = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!accessToken || !refreshToken) {
      logout();
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_LORAX_API_BASE_URL}/token/verify/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: accessToken }),
        },
      );

      if (!response.ok) {
        throw new Error('Access token is invalid or expired');
      }

      const data = await response.json();
      setAuthState({
        isLoggedIn: true,
        groups: Array.isArray(data.groups)
          ? data.groups
          : data.groups
            ? data.groups.split(',').filter(Boolean)
            : ['default'],
        permissions: data.permissions || [],
        isLoading: false,
      });
    } catch {
      try {
        const refreshResponse = await fetch(
          `${process.env.REACT_APP_LORAX_API_BASE_URL}/token/refresh/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
          },
        );

        if (!refreshResponse.ok) {
          throw new Error('Refresh token is invalid or expired');
        }

        const refreshData = await refreshResponse.json();

        localStorage.setItem('accessToken', refreshData.access);

        const revalidateResponse = await fetch(
          `${process.env.REACT_APP_LORAX_API_BASE_URL}/token/verify/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: refreshData.access }),
          },
        );

        if (!revalidateResponse.ok) {
          throw new Error('Re-validation failed after refreshing token');
        }

        const revalidateData = await revalidateResponse.json();
        setAuthState({
          isLoggedIn: true,
          groups: Array.isArray(revalidateData.groups)
            ? revalidateData.groups
            : revalidateData.groups
              ? revalidateData.groups.split(',').filter(Boolean)
              : ['default'],
          permissions: revalidateData.permissions || [],
        });
      } catch {
        logout();
      } finally {
        // Si rien n’a mis isLoading=false (ex: early return), on le fait ici
        setAuthState((s) => ({ ...s, isLoading: false }));
      }
    }
  };

  useEffect(() => {
    validateToken();
  }, []);
  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        logout,
        applyTokens,
        isLoading: authState.isLoading,
      }}
    >
      {/* <Captcha ref={captchaRef} /> */}
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
